import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthenticationError, MissingResourceError, trackError, ValidationError } from '#api/errors.ts';
import { Code } from '#components/atoms/code/index.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import type { Admin } from '#dn-types/admin.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useDeleteAdminMutation } from './useDeleteAdminMutation.ts';

type Props = {
  admin: Admin;
  onCancel: () => void;
  onDelete: () => void;
};

export function DeleteAdminDialog({ admin, onCancel, onDelete }: Props) {
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);
  const deleteAdmin = useDeleteAdminMutation();
  const navigate = useNavigate();
  const location = useAppLocation();

  function closeModal() {
    setValidationErrors(null);
    onCancel();
  }

  return (
    <ConfirmDialog
      title={<div className="truncate">Remove {admin.email}?</div>}
      disableAccept={deleteAdmin.isPending}
      acceptButtonText="Remove"
      onCancel={closeModal}
      onAccept={async () => {
        setValidationErrors(null);

        try {
          await deleteAdmin.mutateAsync(admin.id);
          onDelete();
        } catch (error: unknown) {
          if (error instanceof AuthenticationError && error.code === 'ERR_2FA_RENEWAL_REQUIRED') {
            void navigate('/auth/mfa/renew', { state: { from: location } });
          } else if (error instanceof MissingResourceError) {
            setValidationErrors(['This admin has already been removed']);
          } else if (error instanceof ValidationError) {
            setValidationErrors(error.errors.map((e) => e.message));
          } else {
            setValidationErrors(['An unexpected error occurred']);
            trackError(error);
          }
        }
      }}
      shouldRestoreFocusViaFocusScope={true}
    >
      <p className="p mt-0">This admin will no longer be able to sign in or manage this organization.</p>
      <p className="p mb-0">
        Are you sure you want to remove <Code className="ml-6 wrap-anywhere">{admin.email}</Code>?
      </p>

      {validationErrors?.length ?
        validationErrors.map((message, index) => (
          // eslint-disable-next-line react/no-array-index-key -- It's fine, these aren't going to move/change
          <ErrorMessage key={index} className={index === 0 ? 'mt-32' : undefined}>
            {message}
          </ErrorMessage>
        ))
      : null}
    </ConfirmDialog>
  );
}
